import {
	extractSections,
	findSectionByAnchor,
	linesAddedVsAi,
	linesAddedVsHuman,
	prepareMarkdownForBlocks,
} from "./dualLensMarkdownDiff";

const PLACEHOLDER_MISSING_AI = "Not in AI View";
const PLACEHOLDER_MISSING_HUMAN = "Not in Human View";

function contentCell(content, humanAdded, scrollAdded = new Set()) {
	return {
		type: "content",
		content,
		humanAddedLines: [...humanAdded],
		scrollAddedLines: [...scrollAdded],
	};
}

function placeholderCell(label, variant) {
	return { type: "placeholder", label, variant };
}

function mergeSectionAnchors(primary, secondary, tertiary) {
	const seen = new Set();
	const order = [];

	const add = (sections) => {
		for (const section of sections) {
			if (!seen.has(section.anchor)) {
				seen.add(section.anchor);
				order.push(section.anchor);
			}
		}
	};

	add(primary);
	add(secondary);
	add(tertiary);
	return order;
}

export function buildDualLensSpine(aiMd, humanMd, advancedMd, showDiff) {
	const aiSections = extractSections(aiMd);
	const humanSections = extractSections(humanMd);
	const advancedSections = extractSections(advancedMd);

	const orderedAnchors = mergeSectionAnchors(
		advancedSections,
		humanSections,
		aiSections
	);

	if (orderedAnchors.length === 0) {
		const fallback = prepareMarkdownForBlocks(advancedMd || humanMd || aiMd);
		if (!fallback.trim()) return [];

		const humanAdded = showDiff ? linesAddedVsAi(aiMd, humanMd || fallback) : new Set();
		const scrollHumanAdded = showDiff
			? linesAddedVsAi(aiMd, advancedMd || fallback)
			: new Set();
		const scrollOnlyAdded =
			showDiff && advancedMd
				? linesAddedVsHuman(humanMd || "", advancedMd)
				: new Set();

		return [
			{
				rowIndex: 0,
				anchorKey: "__fallback__",
				ai: aiMd
					? contentCell(fallback, new Set())
					: placeholderCell(PLACEHOLDER_MISSING_AI, "missing-ai"),
				human: humanMd
					? contentCell(fallback, humanAdded)
					: placeholderCell(PLACEHOLDER_MISSING_HUMAN, "missing-human"),
				advanced: advancedMd
					? contentCell(fallback, scrollHumanAdded, scrollOnlyAdded)
					: placeholderCell(PLACEHOLDER_MISSING_HUMAN, "missing-human"),
			},
		];
	}

	const rows = [];

	for (const anchor of orderedAnchors) {
		const aiSec = findSectionByAnchor(aiSections, anchor);
		const humanSec = findSectionByAnchor(humanSections, anchor);
		const advancedSec = findSectionByAnchor(advancedSections, anchor);

		const inAi = aiSec !== null;
		const inHuman = humanSec !== null;
		const inAdvanced = advancedSec !== null;

		const aiContent = aiSec?.content ?? "";
		const humanContent = humanSec?.content ?? "";

		const humanAdded =
			showDiff && inHuman ? linesAddedVsAi(aiContent, humanContent) : new Set();

		const scrollContent = advancedSec?.content ?? humanContent;
		const scrollHumanAdded =
			showDiff && (inAdvanced || inHuman)
				? linesAddedVsAi(aiContent, scrollContent)
				: new Set();

		const scrollOnlyAdded =
			showDiff && inAdvanced
				? linesAddedVsHuman(inHuman ? humanContent : "", advancedSec.content)
				: new Set();

		rows.push({
			rowIndex: rows.length,
			anchorKey: anchor,
			ai: inAi
				? contentCell(aiSec.content, new Set())
				: placeholderCell(PLACEHOLDER_MISSING_AI, "missing-ai"),
			human: inHuman
				? contentCell(humanContent, humanAdded)
				: placeholderCell(PLACEHOLDER_MISSING_HUMAN, "missing-human"),
			advanced: inAdvanced
				? contentCell(advancedSec.content, scrollHumanAdded, scrollOnlyAdded)
				: inHuman
					? contentCell(humanContent, humanAdded)
					: placeholderCell(PLACEHOLDER_MISSING_HUMAN, "missing-human"),
		});
	}

	return rows;
}

export function countHumanDiffRows(spineRows) {
	return spineRows.filter(
		(row) => row.human.type === "content" && row.human.humanAddedLines.length > 0
	).length;
}

export function countScrollIdleDiffRows(spineRows) {
	return spineRows.filter(
		(row) => row.advanced.type === "content" && row.advanced.scrollAddedLines.length > 0
	).length;
}

export function countScrollAiDiffRows(spineRows) {
	return spineRows.filter(
		(row) =>
			row.advanced.type === "content" && row.advanced.humanAddedLines.length > 0
	).length;
}
